import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { CheckCircle, XCircle, Clock, AlertCircle, ClipboardList } from "lucide-react";

const STATUSES = ["present", "absent", "late", "excused"];
const STATUS_STYLE = {
  present: { bg: "hsl(149 40% 30%)", icon: CheckCircle },
  absent: { bg: "hsl(0 65% 55%)", icon: XCircle },
  late: { bg: "hsl(32 87% 67%)", icon: Clock },
  excused: { bg: "hsl(156 15% 40%)", icon: AlertCircle },
};

export default function Attendance() {
  const { user } = useAuth();
  const { t } = useT();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [members, setMembers] = useState([]);
  const [existing, setExisting] = useState({});
  const [selections, setSelections] = useState({});
  const [checklistItems, setChecklistItems] = useState([]);
  const [achievements, setAchievements] = useState({}); // {member_id: {items: [bool], note: ""}}
  const [savingAch, setSavingAch] = useState({});

  useEffect(() => { api.get("/programs").then(r => setPrograms(r.data)); }, []);

  useEffect(() => {
    if (!selectedProgram) return;
    api.get("/members").then(r => setMembers(r.data));
    api.get(`/attendance?program_id=${selectedProgram}`).then(r => {
      const m = {}; r.data.forEach(a => m[a.member_id] = a.status);
      setExisting(m); setSelections(m);
    });
    api.get(`/programs/${selectedProgram}/achievements`).then(r => {
      setChecklistItems(r.data?.checklist_items || []);
      const map = {};
      (r.data?.rows || []).forEach(row => {
        map[row.member_id] = { items: row.items || [], note: row.note || "" };
      });
      setAchievements(map);
    }).catch(() => { setChecklistItems([]); setAchievements({}); });
  }, [selectedProgram]);

  const setStatus = (mid, s) => setSelections({ ...selections, [mid]: s });

  const save = async () => {
    const entries = Object.entries(selections).map(([member_id, status]) => ({ member_id, status }));
    if (!entries.length) return;
    try {
      await api.post("/attendance", { program_id: selectedProgram, entries });
      toast.success(t("Attendance saved", "Հաճախումը պահպանված է"));
    } catch { toast.error("Failed"); }
  };

  const toggleAchievement = async (member_id, idx, value) => {
    const current = achievements[member_id] || { items: new Array(checklistItems.length).fill(false), note: "" };
    const items = [...current.items];
    while (items.length < checklistItems.length) items.push(false);
    items[idx] = value;
    const next = { ...current, items };
    setAchievements({ ...achievements, [member_id]: next });
    setSavingAch({ ...savingAch, [member_id]: true });
    try {
      await api.put(`/programs/${selectedProgram}/achievements/${member_id}`, { items: next.items, note: next.note });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingAch(prev => ({ ...prev, [member_id]: false }));
    }
  };

  const setAchievementNote = (member_id, note) => {
    const current = achievements[member_id] || { items: new Array(checklistItems.length).fill(false), note: "" };
    setAchievements({ ...achievements, [member_id]: { ...current, note } });
  };

  const saveAchievementNote = async (member_id) => {
    const current = achievements[member_id];
    if (!current) return;
    try {
      await api.put(`/programs/${selectedProgram}/achievements/${member_id}`, { items: current.items || [], note: current.note || "" });
      toast.success(t("Note saved", "Նշումը պահպանված է"));
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="uppercase-label">{t("Roll Call", "Հաճախումների ցուցակ")}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-1">{t("Attendance", "Հաճախում")}</h1>
      </div>

      <Card className="clay-card p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedProgram} onValueChange={setSelectedProgram}>
            <SelectTrigger className="max-w-md" data-testid="att-program-select"><SelectValue placeholder={t("Select a program", "Ընտրել ծրագիր")}/></SelectTrigger>
            <SelectContent>
              {programs.map(p => <SelectItem key={p.program_id} value={p.program_id}>{p.title} — {p.date}</SelectItem>)}
            </SelectContent>
          </Select>
          {selectedProgram && <Button className="btn-pill bg-[hsl(149,40%,30%)]" onClick={save} data-testid="att-save">{t("Save Attendance", "Պահպանել հաճախումը")}</Button>}
        </div>
        {selectedProgram && checklistItems.length > 0 && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-[hsl(32,87%,55%)]/10 border border-[hsl(32,87%,55%)]/40 text-sm">
            <ClipboardList size={16} className="mt-0.5 flex-shrink-0 text-[hsl(32,87%,55%)]"/>
            <div>
              <div className="font-bold text-[hsl(32,87%,30%)]">{t("Seminar achievements for this program", "Այս ծրագրի սեմինարի ձեռքբերումները")}</div>
              <div className="text-xs text-muted-foreground">{checklistItems.join(" · ")}</div>
            </div>
          </div>
        )}
      </Card>

      {selectedProgram && (
        <Card className="clay-card p-6">
          <div className="space-y-3">
            {members.map(m => {
              const ach = achievements[m.member_id] || { items: [], note: "" };
              const ticks = checklistItems.map((_, i) => !!(ach.items || [])[i]);
              const completed = ticks.filter(Boolean).length;
              return (
                <div key={m.member_id} className="p-3 rounded-xl bg-muted/40 border border-border" data-testid={`att-row-${m.member_id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[hsl(12,65%,63%)]/20 text-[hsl(12,65%,63%)] flex items-center justify-center font-bold">{(m.full_name || "?")[0]}</div>
                      <div>
                        <div className="font-semibold text-sm">{m.full_name}</div>
                        <div className="text-xs text-muted-foreground">{m.section}{m.patrol ? ` · ${m.patrol}` : ""}</div>
                      </div>
                      {checklistItems.length > 0 && (
                        <Badge variant="outline" className="rounded-full text-[10px]">
                          {completed} / {checklistItems.length} {t("done", "ավարտած")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {STATUSES.map(s => {
                        const S = STATUS_STYLE[s];
                        const active = selections[m.member_id] === s;
                        return (
                          <button
                            key={s}
                            onClick={() => setStatus(m.member_id, s)}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${active ? "text-white" : "bg-muted hover:bg-muted/80 text-muted-foreground"}`}
                            style={active ? { background: S.bg } : {}}
                            data-testid={`att-${m.member_id}-${s}`}
                          >{s}</button>
                        );
                      })}
                    </div>
                  </div>
                  {checklistItems.length > 0 && selections[m.member_id] && selections[m.member_id] !== "absent" && (
                    <div className="mt-3 pl-12">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {checklistItems.map((item, i) => (
                          <label key={`${i}-${item}`} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border cursor-pointer hover:bg-muted/60 transition-colors text-sm">
                            <Checkbox checked={ticks[i]} onCheckedChange={(v) => toggleAchievement(m.member_id, i, !!v)} data-testid={`ach-${m.member_id}-${i}`}/>
                            <span className={ticks[i] ? "font-semibold text-[hsl(149,40%,30%)]" : "text-muted-foreground"}>{item}</span>
                          </label>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder={t("Note for this member (optional)", "Նշում այս անդամի համար (ոչ պարտադիր)")}
                          value={ach.note || ""}
                          onChange={e => setAchievementNote(m.member_id, e.target.value)}
                          onBlur={() => saveAchievementNote(m.member_id)}
                          className="text-xs"
                          data-testid={`ach-note-${m.member_id}`}
                        />
                        {savingAch[m.member_id] && <span className="text-[10px] text-muted-foreground self-center">saving…</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
